import {
  Children,
  cloneElement,
  isValidElement,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
} from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FormFieldProps {
  label: ReactNode;
  /** ID du champ (lié à `<label htmlFor>` et auto-câble aria-describedby/aria-invalid sur l'enfant). */
  htmlFor: string;
  helperText?: ReactNode;
  /** Message d'erreur (déclenche aria-invalid sur l'enfant et role="alert"). */
  error?: ReactNode;
  required?: boolean;
  /** Marque le champ comme optionnel (label ajoute "optionnel" en gris). */
  optional?: boolean;
  /** Icône Lucide rendue en prefix dans le wrapper de l'input. */
  icon?: LucideIcon;
  /** Texte/élément rendu après l'input (ex: "FCFA", "%"). */
  suffix?: ReactNode;
  /** Texte/élément rendu avant l'input (ex: "+225"). */
  prefix?: ReactNode;
  /** Cache le label visuellement mais le garde pour les lecteurs d'écran. */
  labelHidden?: boolean;
  className?: string;
  children: ReactNode;
}

interface InjectableProps extends HTMLAttributes<HTMLElement> {
  id?: string;
  'aria-invalid'?: boolean;
  'aria-required'?: boolean;
}

/**
 * Wrapper standardisé pour un champ de formulaire.
 *
 * Gère label + helper + error + required marker + suffix/prefix + icon.
 * Auto-câble `id`, `aria-invalid` et `aria-describedby` sur l'élément enfant
 * (input/select/textarea) si c'est un seul élément React valide.
 *
 * Conformité WCAG : `<label htmlFor>` obligatoire, error annoncée via `role="alert"`.
 *
 * @example
 *   <FormField label="Email" htmlFor="email" required helperText="Pour recevoir vos factures.">
 *     <Input id="email" type="email" />
 *   </FormField>
 *
 *   <FormField label="Téléphone" htmlFor="phone" prefix="+225" error={errors.phone}>
 *     <Input id="phone" type="tel" />
 *   </FormField>
 */
export function FormField({
  label,
  htmlFor,
  helperText,
  error,
  required = false,
  optional = false,
  icon: Icon,
  suffix,
  prefix,
  labelHidden = false,
  className,
  children,
}: FormFieldProps) {
  const helperId = `${htmlFor}-help`;
  const errorId = `${htmlFor}-error`;
  const describedBy = [error ? errorId : null, !error && helperText ? helperId : null]
    .filter(Boolean)
    .join(' ');

  // Auto-injecte id + aria-* sur l'enfant unique (si c'est un élément React valide).
  const childArray = Children.toArray(children);
  const wrappedChildren =
    childArray.length === 1 && isValidElement(childArray[0])
      ? cloneElement(childArray[0] as ReactElement<InjectableProps>, {
          id: (childArray[0] as ReactElement<InjectableProps>).props.id ?? htmlFor,
          'aria-invalid': error ? true : undefined,
          'aria-required': required ? true : undefined,
          ...(describedBy ? { 'aria-describedby': describedBy } : {}),
        })
      : children;

  const hasInputAdornment = Icon || prefix || suffix;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label
        htmlFor={htmlFor}
        className={cn(
          'inline-flex items-center gap-1.5 text-[13px] font-medium text-charcoal-900 dark:text-charcoal-100',
          labelHidden && 'sr-only',
        )}
      >
        <span>{label}</span>
        {required && (
          <span aria-hidden className="text-rose-600 dark:text-rose-400">
            *
          </span>
        )}
        {optional && !required && (
          <span className="font-normal text-slate-400 dark:text-charcoal-400">(optionnel)</span>
        )}
      </label>

      {hasInputAdornment ? (
        <div className="relative flex items-stretch">
          {Icon && (
            <span
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-charcoal-400"
            >
              <Icon className="h-4 w-4" strokeWidth={1.75} />
            </span>
          )}
          {prefix && (
            <span className="inline-flex items-center rounded-l-md border border-r-0 border-slate-200 bg-slate-50 px-3 text-[13px] text-slate-600 dark:border-charcoal-700 dark:bg-charcoal-800 dark:text-charcoal-300">
              {prefix}
            </span>
          )}
          <div
            className={cn(
              'relative flex-1',
              Icon && '[&>input]:pl-9 [&>select]:pl-9',
              prefix && '[&>input]:rounded-l-none [&>select]:rounded-l-none',
              suffix && '[&>input]:rounded-r-none [&>select]:rounded-r-none',
            )}
          >
            {wrappedChildren}
          </div>
          {suffix && (
            <span className="inline-flex items-center rounded-r-md border border-l-0 border-slate-200 bg-slate-50 px-3 text-[13px] text-slate-600 dark:border-charcoal-700 dark:bg-charcoal-800 dark:text-charcoal-300">
              {suffix}
            </span>
          )}
        </div>
      ) : (
        wrappedChildren
      )}

      {error ? (
        <p id={errorId} role="alert" className="text-[11px] text-rose-700 dark:text-rose-400">
          {error}
        </p>
      ) : helperText ? (
        <p id={helperId} className="text-xs text-slate-500 dark:text-charcoal-300">
          {helperText}
        </p>
      ) : null}
    </div>
  );
}
